import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { takeUntil } from 'rxjs'

import { AvatarComponent } from "../../avatar/avatar.component"
import { ResponsiveComponent } from '@nx-superprep/shared'
import { UserService } from '@nx-superprep/backend/firebase'
import { log } from '@nx-superprep/utils'

@Component({
  selector: 'pop-ui-profile-edit',
  standalone: true,
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  imports: [
    NgClass, NgStyle, NgTemplateOutlet,
    AvatarComponent,
    ReactiveFormsModule,
  ]
})
export class ProfileEditComponent extends ResponsiveComponent implements OnInit, OnDestroy {

  @Input() isOpen = true
  @Output() isOpenChange = new EventEmitter<boolean>()

  avatar_src = '/assets/images/avatar-ellipse.png'
  avatar_alt = 'Avatar-ellipse'

  private _error: unknown

  hover = false
  isSubmitted = false

  editForm!: FormGroup
  resetForm!: FormGroup
  forgotForm!: FormGroup

  private _form!: FormGroup
  get form() { return this._form }
  set form(value) { this._form = value }

  get title() {
    return this.form === this.resetForm ? 'Change Password' : 'Update Profile'
  }

  get isUserActive() { return this.userService.isUserActive; }
  get isUserAnonymous() { return this.userService.isAnonymous; }
  get isUserGuest() { return this.userService.isGuest; }

  // convenience getters for easy access to form fields
  get email() {
    return this.editForm.get('email')
  }
  get profileName() {
    return this.editForm.get('profile')
  }
  get firstName() {
    return this.editForm.get('firstName')
  }
  get lastName() {
    return this.editForm.get('lastName')
  }
  //
  get currentPassord() {
    return this.form.get('currentPassword')
  }
  get newPassord() {
    return this.resetForm.get('newPassword')
  }
  get confirmPassord() {
    return this.resetForm.get('confirmPassword')
  }

  errorMessage(form: FormGroup) {
    return form.dirty ? (this._error as Record<string, string>)['message'] : undefined
  }

  error(form: FormGroup) {
    return form.dirty ? this._error : undefined
  }

  isInvalid(form: FormGroup, name: string) {
    return form.get(name)?.invalid && (form.get(name)?.dirty || form.get(name)?.touched)
  }

  isRequired(form: FormGroup, name: string) {
    return form.get(name)?.errors?.['required']
  }

  onClose() {
    this.isOpen = false
    this.isOpenChange.emit(this.isOpen)
  }

  onEdit() {
    log.debug('Edit')
  }

  onUpdate() {
    log.debug('Update')
  }

  onReset() {
    this._error = undefined
  }

  onAbort() {
    this.form.reset()
    this.form = this.editForm
  }

  onError(error: unknown) {
    log.debug(error)
    this._error = error
    this.isSubmitted = false
  }

  async onContinue(form: FormGroup) {
    log.debug(form)
    this._error = undefined
  }

  onToggle(form: FormGroup) {
    log.debug(form)
    this.form = form
  }

  async onSubmit(form: FormGroup) {
    if (this.isSubmitted || form.pristine || form.invalid) {
      return
    }
    this.isSubmitted = true
    this._error = undefined
    let promises: Promise<void>[] = []
    switch(form) {
      case this.resetForm: {
        const newPassord = this.newPassord?.value
        const password = this.currentPassord?.value
        promises = [this.userService.changePassword(newPassord, password).then(success => {if (!success) { this.form.reset() }})]
      } break
      case this.editForm: {
        const profileName = form.get('profile')?.value ?? '';
        const firstName = form.get('firstName')?.value ?? ''
        const lastName = form.get('lastName')?.value ?? ''
        const email = !!form.get('email')?.value && form.get('email')?.dirty ? form.get('email')?.value : undefined
        const password = this.currentPassord?.value
        promises = [
          this.userService.changeEmail(email, password).then(success => { if (!success) { this.email?.reset(); this.currentPassord?.reset() }}),
          this.userService.setInfo({ firstName, lastName, profileName }).then(success => { if (!success) { this.form.reset() }})
        ]
      } break
      default: promises = [Promise.resolve()]
    }
    await Promise.all(promises).finally(() => {
      this.isSubmitted = false
      form.reset({...form.value, email:this.userService.activeUser?.email})
    })
  }

  override ngOnInit() {
    super.ngOnInit()
    const email = { email: ['', [Validators.required, Validators.email]] }
    this.editForm = this.formBuilder.group({
      firstName: ['', [Validators.nullValidator]],
      lastName: ['', [Validators.nullValidator]],
      profile: ['', [Validators.nullValidator]],
      ...email,
      currentPassword: [''],
    })
    this.resetForm = this.formBuilder.group({
      currentPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
    })
    this.forgotForm = this.formBuilder.group({
      ...email
    })
    //
    this.email?.setValue(this.userService.activeUser?.email)
    this.userService.getInfo().pipe(takeUntil(this.onDestroy)).subscribe(info => {
      this.profileName?.setValue(info.profileName)
      this.firstName?.setValue(info.firstName)
      this.lastName?.setValue(info.lastName)
    })
    this.form = this.editForm
  }

  readonly userService = inject(UserService)
  readonly formBuilder = inject(FormBuilder)

  constructor() {
    super()
    this.rootClassName = 'edit-root-class-name'
  }
}
